import { resolveErrorResponseMessage } from '@repo/ui/lib/error-messages'
import axios from 'axios'
import { toast } from 'sonner'

import type {
  AddonInput,
  AdminOverview,
  AISpendOverview,
  AIUsageSummary,
  ComplianceAuditInput,
  ComplianceState,
  CreateContractInput,
  DowngradeConfirmInput,
  DowngradePreview,
  EvolutionWebhookResult,
  GrantCourtesyInput,
  InvoiceList,
  LinkInstanceInput,
  MetaChannelStatus,
  OrganizationAuditEvent,
  OrganizationDirectory,
  OrganizationSettings,
  OrgConsentEnforcement,
  PlatformIntegration,
  PrivacyInventory,
  PrivacyOperation,
  PromoPopup,
  PromoPopupInput,
  RevenueOverview,
  RevenueSyncResult,
  UpgradeConfirmInput,
  UpgradePlan,
  UpgradePreview,
} from '@/lib/api-types'

const DEFAULT_API_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:5050'

let adminToken: string | null = null
let apiOrigin = DEFAULT_API_URL.replace(/\/$/, '')

export const adminApi = axios.create({
  baseURL: `${apiOrigin}/v1`,
  headers: { 'Content-Type': 'application/json' },
})

export function configureAdminApi(params: {
  apiUrl?: string | null
  token?: string | null
}) {
  apiOrigin = (params.apiUrl || DEFAULT_API_URL).replace(/\/$/, '')
  adminToken = params.token?.trim() || null
  adminApi.defaults.baseURL = `${apiOrigin}/v1`
}

export function getApiOrigin() {
  return apiOrigin
}

adminApi.interceptors.request.use((config) => {
  if (adminToken) {
    config.headers.Authorization = `Bearer ${adminToken}`
  }
  return config
})

adminApi.interceptors.response.use(
  (response) => response,
  (error: unknown) => {
    if (!axios.isAxiosError(error)) {
      toast.error('Erro inesperado')
      return Promise.reject(error)
    }

    if (!error.response) {
      toast.error('Não foi possível conectar na API')
      return Promise.reject(error)
    }

    toast.error(
      resolveErrorResponseMessage(error.response.data, error.response.status)
    )
    return Promise.reject(error)
  }
)

export async function fetchAdminOverview() {
  const { data } = await adminApi.get<AdminOverview>('/api/admin/overview')
  return data
}

export async function fetchRevenue(months = 12) {
  const { data } = await adminApi.get<RevenueOverview>('/api/admin/revenue', {
    params: { months },
  })
  return data
}

export async function fetchInvoices(params: {
  status?: string
  organizationId?: string
  search?: string
  page?: number
  pageSize?: number
}) {
  const { data } = await adminApi.get<InvoiceList>(
    '/api/admin/revenue/invoices',
    {
      params: {
        status: params.status || undefined,
        organizationId: params.organizationId || undefined,
        search: params.search || undefined,
        page: params.page,
        pageSize: params.pageSize,
      },
    }
  )
  return data
}

export async function syncRevenue() {
  const { data } = await adminApi.post<RevenueSyncResult>(
    '/api/admin/revenue/sync'
  )
  return data
}

export async function fetchOrganizationSettings(orgId: string) {
  const { data } = await adminApi.get<OrganizationSettings>(
    `/api/admin/organization/${orgId}/settings`
  )
  return data
}

export async function fetchOrganizationDirectory(orgId: string) {
  const { data } = await adminApi.get<OrganizationDirectory>(
    `/api/admin/organization/${orgId}/directory`
  )
  return data
}

export async function fetchBillingAudit(orgId: string) {
  const { data } = await adminApi.get(
    `/api/admin/organization/${orgId}/billing-audit`
  )
  return data as Array<{
    id: string
    organizationId: string
    actorUserId: string | null
    type: string
    metadata: unknown
    createdAt: string
  }>
}

export async function syncStripePeriod(orgId: string) {
  const { data } = await adminApi.post(
    `/api/admin/organization/${orgId}/sync-stripe-period`
  )
  return data
}

export async function grantCourtesy(params: GrantCourtesyInput) {
  const { data } = await adminApi.post(
    `/api/admin/organization/${params.orgId}/plan`,
    {
      plan: 'COURTESY',
      status: params.status ?? 'ACTIVE',
      currentPeriodEnd: params.currentPeriodEnd || null,
      skipOnboarding: params.skipOnboarding ?? true,
      limits: params.limits,
      reason: params.reason,
      actorUserId: params.actorUserId,
    }
  )
  return data
}

export async function createContract(
  orgId: string,
  input: CreateContractInput
) {
  const { data } = await adminApi.post(
    `/api/admin/organization/${orgId}/contracts`,
    input
  )
  return data
}

export async function previewUpgrade(orgId: string, targetPlan: UpgradePlan) {
  const { data } = await adminApi.post<UpgradePreview>(
    `/api/admin/organization/${orgId}/upgrades/preview`,
    { targetPlan }
  )
  return data
}

export async function confirmUpgrade(
  orgId: string,
  input: UpgradeConfirmInput
) {
  const { data } = await adminApi.post(
    `/api/admin/organization/${orgId}/upgrades/confirm`,
    input
  )
  return data as Record<string, unknown>
}

export async function previewDowngrade(orgId: string, targetPlan: string) {
  const { data } = await adminApi.post<DowngradePreview>(
    `/api/admin/organization/${orgId}/downgrades/preview`,
    { targetPlan }
  )
  return data
}

export async function confirmDowngrade(
  orgId: string,
  input: DowngradeConfirmInput
) {
  const { data } = await adminApi.post(
    `/api/admin/organization/${orgId}/downgrades/confirm`,
    input
  )
  return data as Record<string, unknown>
}

export async function upsertAddon(orgId: string, input: AddonInput) {
  const { data } = await adminApi.post(
    `/api/admin/organization/${orgId}/addons`,
    input
  )
  return data as Record<string, unknown>
}

export async function transferOwner(orgId: string, userId: string) {
  const { data } = await adminApi.post(
    `/api/admin/organization/${orgId}/owner`,
    { userId }
  )
  return data as Record<string, unknown>
}

export async function updateBillingOwner(orgId: string, userId: string) {
  const { data } = await adminApi.post(
    `/api/admin/organization/${orgId}/billing-owner`,
    { userId }
  )
  return data as Record<string, unknown>
}

export async function fetchOrganizationDeletionPreview(orgId: string) {
  const { data } = await adminApi.get<PrivacyInventory>(
    `/api/admin/organization/${orgId}/deletion/preview`
  )
  return data
}

export async function fetchOrganizationDeletion(orgId: string) {
  const { data } = await adminApi.get<PrivacyOperation | null>(
    `/api/admin/organization/${orgId}/deletion`
  )
  return data
}

/**
 * Abre a operação de exclusão do tenant (offboarding completo) e a executa;
 * devolve a operação — que pode ainda estar em andamento.
 */
export async function deleteOrganization(orgId: string, reason?: string) {
  const { data } = await adminApi.delete<PrivacyOperation>(
    `/api/admin/organization/${orgId}`,
    { data: { reason } }
  )
  return data
}

export async function retryPrivacyOperation(
  id: string,
  options: { skipExternal?: boolean } = {}
) {
  const { data } = await adminApi.post<PrivacyOperation>(
    `/api/admin/organization/privacy-operations/${id}/retry`,
    options
  )
  return data
}

export async function fetchAgentsUsageSummary(params: {
  orgId: string
  days?: number
  agentId?: string
}) {
  const { data } = await adminApi.get<AIUsageSummary>(
    `/api/admin/agents/${params.orgId}/usage-summary`,
    { params: { days: params.days, agentId: params.agentId || undefined } }
  )
  return data
}

export async function fetchAiSpendOverview(params: {
  days: number
  source?: string
  model?: string
  limit?: number
}) {
  const { data } = await adminApi.get<AISpendOverview>(
    '/api/admin/ai-spend/overview',
    {
      params: {
        days: params.days,
        source: params.source || undefined,
        model: params.model || undefined,
        limit: params.limit,
      },
    }
  )
  return data
}

export async function linkEvolutionInstance(
  orgId: string,
  input: LinkInstanceInput
) {
  const { data } = await adminApi.post<EvolutionWebhookResult>(
    `/api/admin/evolution/${orgId}/link-instance`,
    input
  )
  return data
}

export async function refreshEvolutionWebhook(
  orgId: string,
  channelId: string
) {
  const { data } = await adminApi.post<EvolutionWebhookResult>(
    `/api/admin/evolution/${orgId}/${channelId}/refresh-webhook`
  )
  return data
}

export async function fetchMetaChannelStatus(orgId: string, channelId: string) {
  const { data } = await adminApi.get<MetaChannelStatus>(
    `/api/admin/meta/${orgId}/${channelId}/status`
  )
  return data
}

export async function fetchMetaSubscription(orgId: string, channelId: string) {
  const { data } = await adminApi.get(
    `/api/admin/meta/${orgId}/${channelId}/subscription`
  )
  return data as {
    apps: Array<{
      whatsapp_business_api_data?: { id?: string; name?: string; link?: string }
    }>
  }
}

export async function retryMetaContacts(orgId: string, channelId: string) {
  const { data } = await adminApi.post(
    `/api/admin/meta/${orgId}/${channelId}/retry-contacts`
  )
  return data as { requested: boolean; requestId: string }
}

export async function prepareMetaOffboarding(orgId: string, channelId: string) {
  const { data } = await adminApi.post(
    `/api/admin/meta/${orgId}/${channelId}/prepare-offboarding`
  )
  return data as {
    destructiveActionExecuted: boolean
    steps: string[]
  }
}

export async function updateStripePayer(params: {
  orgId: string
  stripeCustomerId: string
}) {
  const { data } = await adminApi.post(
    `/api/admin/organization/${params.orgId}/stripe-payer`,
    { stripeCustomerId: params.stripeCustomerId }
  )
  return data
}

export async function backfillStripePeriods(dryRun = true) {
  const { data } = await adminApi.post(
    '/api/admin/organization/backfill-stripe-periods',
    { dryRun }
  )
  return data as {
    scanned: number
    updated: number
    dryRun: boolean
    items: Array<{
      organizationId: string
      currentPeriodStart: string | null
      currentPeriodEnd: string | null
      updated: boolean
      error?: string
    }>
  }
}

export async function fetchPromoPopups(params?: {
  search?: string
  status?: 'all' | 'active' | 'inactive'
}) {
  const { data } = await adminApi.get('/api/admin/promo-popups', {
    params: {
      ...(params?.search ? { search: params.search } : {}),
      ...(params?.status && params.status !== 'all'
        ? { status: params.status }
        : {}),
    },
  })
  return data as PromoPopup[]
}

export async function fetchPromoPopup(id: string) {
  const { data } = await adminApi.get(`/api/admin/promo-popups/${id}`)
  return data as PromoPopup
}

export async function createPromoPopup(input: PromoPopupInput) {
  const { data } = await adminApi.post('/api/admin/promo-popups', input)
  return data as PromoPopup
}

export async function updatePromoPopup(id: string, input: PromoPopupInput) {
  const { data } = await adminApi.put(`/api/admin/promo-popups/${id}`, input)
  return data as PromoPopup
}

export async function deletePromoPopup(id: string) {
  const { data } = await adminApi.delete(`/api/admin/promo-popups/${id}`)
  return data as { success: boolean }
}

export async function uploadPromoPopupImage(file: File) {
  const form = new FormData()
  form.append('file', file)
  const { data } = await adminApi.post(
    '/api/admin/promo-popups/uploads',
    form,
    {
      // A instancia define Content-Type JSON por padrao; remover deixa o axios
      // montar o multipart com o boundary correto.
      headers: { 'Content-Type': undefined },
    }
  )
  return data as { url: string }
}

export async function fetchCompliance(orgId: string) {
  const { data } = await adminApi.get<ComplianceState>(
    `/api/admin/organization/${orgId}/compliance`
  )
  return data
}

export async function fetchComplianceAudit(orgId: string) {
  const { data } = await adminApi.get<OrganizationAuditEvent[]>(
    `/api/admin/organization/${orgId}/compliance/audit-events`
  )
  return data
}

export async function setConsentEnforcement(
  orgId: string,
  input: ComplianceAuditInput & { mode: OrgConsentEnforcement | null }
) {
  const { data } = await adminApi.put<{
    consentEnforcement: OrgConsentEnforcement | null
    changed: boolean
  }>(`/api/admin/organization/${orgId}/compliance/consent-enforcement`, input)
  return data
}

export async function setPromptOptimizerExcluded(
  orgId: string,
  input: ComplianceAuditInput & { excluded: boolean }
) {
  const { data } = await adminApi.put<{
    promptOptimizerExcluded: boolean
    changed: boolean
  }>(`/api/admin/organization/${orgId}/compliance/prompt-optimizer`, input)
  return data
}

export async function setIntegrationAllowed(
  orgId: string,
  integration: PlatformIntegration,
  input: ComplianceAuditInput & { allowed: boolean }
) {
  const { data } = await adminApi.put<{
    integration: PlatformIntegration
    allowed: boolean
    changed: boolean
  }>(
    `/api/admin/organization/${orgId}/compliance/integrations/${integration}`,
    input
  )
  return data
}
